/* eslint-disable max-lines */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { OfferKind, TrackingActivity } from '@recruitee/http-types';
import { Colors, IconName, keyIconMap, NestedIcon } from '@recruitee/user-interface';
import { deepClone } from '@recruitee/utils';

export const ActivityIcons = keyIconMap({
  assignment_add: 'person-pin',
  assignment_remove: 'delete',
  candidate_accepted_retention_period: 'gdpr-candidate',
  candidate_add: 'person-add',
  candidate_apply: 'person-add',
  candidate_attachment_remove: 'attach-file',
  candidate_attachment_upload: 'attach-file',
  candidate_auto_disqualify: 'block',
  candidate_call_add: 'phone',
  candidate_call_destroy: 'phone',
  candidate_declined_retention_period: 'gdpr-candidate',
  candidate_disqualify: 'block',
  candidate_expiring: 'gdpr-candidate',
  candidate_hired: 'hired',
  candidate_field_created: 'account-box-outline',
  candidate_field_deleted: 'account-box-outline',
  candidate_field_updated: 'account-box-outline',
  candidate_follower_add: 'bookmark',
  candidate_follower_remove: 'bookmark-border',
  candidate_gone_overdue: 'access-time',
  candidate_import: 'person',
  candidate_mail_incoming: 'reply',
  candidate_mail_outgoing: 'send',
  candidate_meeting_add: 'person-pin',
  candidate_meeting_destroy: 'person-pin',
  candidate_merge: 'merge-type',
  candidate_message_remove: 'delete',
  candidate_note_add: 'chat',
  candidate_note_remove: 'delete',
  candidate_offer_assign: 'next-week',
  candidate_offer_remove: 'delete',
  candidate_overdue: 'access-time',
  candidate_referred: 'navbar-referrals',
  candidate_remove: 'person',
  candidate_requalify: 'loop',
  candidate_review: 'star',
  candidate_review_remove: 'star-border',
  candidate_stage_change: 'forward',
  candidate_tags: 'label',
  candidate_tags_remove: 'label-outline',
  candidate_access_revoked: 'shared-with-me',
  candidate_access_granted: 'shared-with-me',
  candidate_access_received: 'person-add',
  disqualify_reason_add: 'block',
  disqualify_reason_remove: 'delete',
  eeo_admin_sent_data_request_link: 'non-federal-company',
  eeo_candidate_completed_data_request: 'non-federal-company',
  eeo_candidate_skipped_data_request: 'non-federal-company',
  eeo_data_request_link_sent: 'non-federal-company',
  gdpr_consent_link_sent: 'gdpr-candidate',
  gdpr_custom_expires_at_updated: 'gdpr-candidate',
  gdpr_request_add: 'gdpr-candidate',
  import_finish: 'import-export',
  import_remove: 'import-export',
  import_revert: 'import-export',
  interview_event_change: 'event',
  interview_event_deleted: 'delete',
  interview_event_remove: 'delete',
  interview_event_request_sent: 'event',
  interview_event_rescheduled: 'event',
  interview_event_scheduled: 'event',
  interview_requested_event_add: 'event',
  interview_requested_result_add: 'assignment-turned-in',
  interview_result_add: 'thumbs-up-down',
  interview_result_request_sent: 'assignment-turned-in',
  interview_result_requested: 'timer-sand',
  interview_result_requests_add: 'timer-sand',
  interview_result_remove: 'thumbs-up-down',
  interview_result_update: 'thumbs-up-down',
  job_add: {
    default: 'work',
    talentPool: 'talent-pools',
  },
  job_attachment_remove: 'delete',
  job_attachment_upload: 'attach-file',
  job_automatically_created_from_requisition: 'work',
  job_auto_publish_fail: {
    default: 'error-outline',
    requisition: 'assignment-turned-in',
  },
  job_auto_close_fail: 'error-outline',
  job_expiring_candidates: 'gdpr-candidate',
  job_follower_add: 'bookmark',
  job_follower_remove: 'bookmark-border',
  job_hiring_manager_change: 'person',
  job_hiring_manager_clear: 'delete',
  job_location_updated: 'place',
  job_note_add: 'chat',
  job_note_remove: 'delete',
  job_overdue_candidates: 'access-time',
  job_published_requisition_mismatch: 'assignment-turned-in',
  job_recruiter_change: 'person',
  job_recruiter_clear: 'delete',
  job_remove: 'delete',
  job_requisition_archived: 'assignment-turned-in',
  job_requisition_filled: 'assignment-turned-in',
  job_requisition_opening_removed: 'assignment-turned-in',
  job_requisition_retrieved: 'assignment-turned-in',
  job_scheduler_change: 'calendar-today',
  job_scheduler_clear: 'delete',
  job_status_auto_change: 'work',
  job_status_change: 'remove-red-eye',
  job_tags: 'label',
  job_tags_remove: 'label-outline',
  job_work_model_updated: 'domain',
  location_archived: 'archive',
  location_assigned_job_archived: 'archive',
  location_assigned_job_retrieved: 'place',
  location_assigned_requisition_archived: 'archive',
  location_assigned_requisition_retrieved: 'place',
  location_created: 'place',
  location_deleted: 'place',
  location_retrieved: 'place',
  location_updated: 'place',
  mailbox_admins_added: 'person-add',
  mailbox_attachment_converted: 'sync',
  mailbox_bulk_message_sent: 'call-split',
  mailbox_message_forwarded: 'call-made',
  mailbox_message_received: 'call-received',
  mailbox_message_sent: 'call-made',
  mailbox_reply_received: 'call-received',
  mailbox_reply_sent: 'call-made',
  mailbox_thread_archived: 'archive',
  mailbox_thread_converted: 'sync',
  mailbox_thread_removed: 'delete',
  mailbox_thread_pinned: 'pin',
  mailbox_thread_unarchived: 'unarchive',
  mailbox_threads_archived: 'archive',
  mailbox_threads_unarchived: 'unarchive',
  member_add: 'person-outline',
  member_remove: 'delete',
  member_role_change: 'vpn-key',
  note_mention: {
    default: 'mention',
    requisition: 'assignment-turned-in',
  },
  note_reaction: {
    default: 'add-reaction',
    requisition: 'assignment-turned-in',
  },
  note_reply: {
    default: 'add-reaction',
    requisition: 'assignment-turned-in',
  },
  placement_location_updated: 'place',
  referral_offer_status_change: 'navbar-referrals',
  referral_program_updated: 'navbar-referrals',
  referral_reward_assigned: 'reward',
  referral_reward_claimed: 'reward',
  referral_reward_unassigned: 'reward',
  requisition_approval_flow_create: 'assignment-turned-in',
  requisition_approval_flow_changed: 'assignment-turned-in',
  requisition_approval_flow_remove: 'assignment-turned-in',
  requisition_approval_flow_update: 'assignment-turned-in',
  requisition_approval_requested: 'assignment-turned-in',
  requisition_approval_request_canceled: 'assignment-turned-in',
  requisition_approvers_changed: 'assignment-turned-in',
  requisition_approver_decision_approved: 'assignment-turned-in',
  requisition_approver_decision_canceled: 'assignment-turned-in',
  requisition_approver_decision_pending: 'assignment-turned-in',
  requisition_approver_decision_rejected: 'assignment-turned-in',
  requisition_approver_decision_reminder: 'assignment-turned-in',
  requisition_archived: 'assignment-turned-in',
  requisition_assigned: 'assignment-turned-in',
  requisition_assignment_add: 'assignment-turned-in',
  requisition_assignment_remove: 'assignment-turned-in',
  requisition_attachment_remove: 'assignment-turned-in',
  requisition_attachment_upload: 'assignment-turned-in',
  requisition_created: 'assignment-turned-in',
  requisition_deleted: 'assignment-turned-in',
  requisition_disabled: 'assignment-turned-in',
  requisition_enabled: 'assignment-turned-in',
  requisition_follower_add: 'assignment-turned-in',
  requisition_follower_remove: 'assignment-turned-in',
  requisition_form_updated: 'assignment-turned-in',
  requisition_hiring_manager_change: 'assignment-turned-in',
  requisition_hiring_manager_clear: 'assignment-turned-in',
  requisition_job_added: 'assignment-turned-in',
  requisition_location_updated: 'place',
  requisition_note_add: 'assignment-turned-in',
  requisition_note_remove: 'assignment-turned-in',
  requisition_opening_filled: 'assignment-turned-in',
  requisition_opening_removed: 'assignment-turned-in',
  requisition_opening_unassigned: 'assignment-turned-in',
  requisition_recruiter_change: 'assignment-turned-in',
  requisition_recruiter_clear: 'assignment-turned-in',
  requisition_retrieved: 'assignment-turned-in',
  requisition_status_change: 'assignment-turned-in',
  requisition_unassigned: 'assignment-turned-in',
  requisition_work_model_updated: 'domain',
  share_container_add: 'share',
  share_container_remove: 'delete',
  share_note_add: 'chat',
  share_result_add: 'thumbs-up-down',
  share_review_add: 'star',
  stage_add: 'label',
  stage_remove: 'delete',
  tag_add: 'label',
  tag_remove: 'label-outline',
  task_add: 'playlist-add-check',
  task_complete: 'check',
  task_due_today: 'check',
  task_overdue: 'check',
  task_remove: 'delete',
  texting_message_sent: 'sms',
  texting_message_sent_by_candidate: 'sms',
  texting_consent_granted_by_candidate: 'sms',
  texting_consent_withdrawn_by_candidate: 'sms',
  texting_consent_added: 'sms',
  texting_consent_withdrawn: 'sms',
  texting_enabled: 'sms',
  texting_disabled: 'sms',
  texting_phone_number_registered: 'sms',
  texting_phone_number_deleted: 'sms',
  texting_phone_number_removed_by_system: 'sms',
  texting_campaign_removed_by_system: 'sms',
});

export const ActivityAvatarIcons = keyIconMap({
  candidate_auto_disqualify: 'automated-actions',
  candidate_expiring: 'people',
  candidate_gone_overdue: 'account-clock',
  candidate_overdue: 'account-clock',
  eeo_data_request_link_sent: 'automated-actions',
  job_auto_publish_fail: 'automated-actions',
  job_auto_close_fail: 'automated-actions',
  job_expiring_candidates: 'people',
  job_overdue_candidates: 'account-clock',
  job_status_auto_change: 'automated-actions',
});

type TActivityAvatarIcon = keyof typeof ActivityAvatarIcons;

type TActivityIcon = keyof typeof ActivityIcons;

@Component({
  selector: 'rt-activity-item',
  templateUrl: './activity-item.component.html',
  styleUrls: ['./activity-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityItemComponent implements OnChanges {
  @Input() public activity: TrackingActivity;
  @Input() public shortMessage: boolean = false;
  @Input() public isSmall: boolean = false;
  @Input() public canManageCandidates: boolean = false;

  @Output() public activityItemClick: EventEmitter<TrackingActivity> = new EventEmitter();
  @Output() public readUnreadClick: EventEmitter<{ read: boolean; id: number }> =
    new EventEmitter();

  public hasDiff: boolean = false;
  public Colors = Colors;
  public activityLocal: TrackingActivity;
  public avatarUrl: string | null;
  public avatarIcon: IconName | null;
  public avatarType: 'image' | 'icon' | 'custom' | 'initials';

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.activity) {
      this.activityLocal = deepClone(this.activity);
      this.hasDiff =
        this.activityLocal.event === 'candidate_field_updated' &&
        !!this.activityLocal.previousField;
      this.avatarIcon = this.getAvatarIcon(this.activityLocal);
      const { type, url } = this.resolveImage(this.activityLocal, this.avatarIcon);
      this.avatarType = type;
      if (url) {
        this.avatarUrl = url;
      }
    }
  }

  public get icon(): IconName {
    const icon = ActivityIcons[<TActivityIcon>this.activityLocal.event];
    if (typeof icon !== 'object') return icon || '';
    return this.resolveNestedIcon(icon);
  }

  public get message(): string {
    return this.shortMessage ? this.activity.messageShortHtml : this.activityLocal.messageHtml;
  }

  public resolveImage(
    activity: TrackingActivity,
    avatarIcon: IconName | null,
  ): {
    type: 'image' | 'icon' | 'custom' | 'initials';
    url?: string;
  } {
    const skipAvatar =
      [
        'candidate_overdue',
        'job_overdue_candidates',
        'candidate_gone_overdue',
        'candidate_expiring',
        'job_expiring_candidates',
        'job_status_auto_change',
        'job_auto_publish_fail',
      ].includes(activity.event) || activity.triggered;

    if (skipAvatar || !activity.avatarUrl) {
      return { type: avatarIcon ? 'icon' : 'custom' };
    } else {
      return activity.hasAvatar ? { type: 'image', url: activity.avatarUrl } : { type: 'initials' };
    }
  }

  public getAvatarIcon(activity: TrackingActivity): IconName | null {
    return ActivityAvatarIcons[<TActivityAvatarIcon>activity.event] || null;
  }

  public hasContent(): boolean {
    return (
      [
        'candidate_mail_incoming',
        'candidate_mail_outgoing',
        'candidate_note_add',
        'job_note_add',
        'share_note_add',
        'note_mention',
        'gdpr_request_add',
      ].includes(this.activityLocal.event) ||
      !!this.activityLocal.attachment ||
      (!!this.activityLocal.mailboxMessage && !!this.activityLocal.mailboxMessage.text) ||
      (!!this.activityLocal.textingMessage && !!this.activityLocal.textingMessage.text)
    );
  }

  public onItemClick(event: MouseEvent): void {
    if (!event.defaultPrevented) {
      this.activityItemClick.emit(this.activity);
    }
  }

  public onMarkAsReadClick(event: MouseEvent): void {
    event.preventDefault();
    this.readUnreadClick.emit({ read: !this.activityLocal.unread, id: this.activityLocal.id });
    this.activityLocal.unread = !this.activityLocal.unread;
    this.cdRef.detectChanges();
  }

  public handleAvatarError() {
    this.avatarType = 'initials';
    this.cdRef.detectChanges();
  }

  private resolveNestedIcon(icon: NestedIcon): IconName {
    if (this.activityLocal.requisition?.id) return icon.requisition || icon.default;
    if (this.activityLocal.offer?.kind === OfferKind.TalentPool)
      return icon.talentPool || icon.default;
    return icon.default;
  }
}
